import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { keyed } from 'lit/directives/keyed.js'
import { customElement, query, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { style } from './create-order-template.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  ORDER_TEMPLATE_TYPES_API,
  GetOrderTemplateTypesRequest,
  type OrderTemplateTypesApi,
  ORDER_TEMPLATE_GROUPS_API,
  GetOrderTemplateGroupsRequest,
  type OrderTemplateGroupsApi,
} from 'components/discovery/pdp/create-order-template/api/create-order-template-api.js'
import { orderTemplateGroup, orderTemplateType } from 'components/discovery/pdp/create-order-template/typing/create-order-template-types.js'
import { ORDER_TEMPLATES_STORE, type OrderTemplatesStore } from 'components/discovery/shared/order-templates/stores/order-templates-store.js'
import { OrderTemplatesLoadingMixin } from 'components/shared/order-templates/order-templates-loading-mixin.js'
import { orderTemplateGroupsPath, orderTemplateTypesPath } from 'components/discovery/shared/endpoint-paths.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'

export const createOrderTemplateHiddenFields = ['submit-button'] as const

@customElement('csc-create-order-template')
export class CSCCreateOrderTemplate extends HiddenFieldsMixin(OrderTemplatesLoadingMixin(LitElement), createOrderTemplateHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(ORDER_TEMPLATE_TYPES_API)
  private typesApi!: OrderTemplateTypesApi

  @inject(ORDER_TEMPLATE_GROUPS_API)
  private groupsApi!: OrderTemplateGroupsApi

  @inject(ORDER_TEMPLATES_STORE)
  private store!: OrderTemplatesStore

  @state()
  private groups: orderTemplateGroup[] = []

  @state()
  private typeOptions: orderTemplateType[] = []

  @state()
  private name = ''

  @state()
  private selectedGroupId: number | string = ''

  @state()
  private selectedType = ''

  @query('cse-text-input')
  private nameInput!: HTMLElement

  @state()
  private loading = false

  @state()
  private formKey = 0

  private typesAndGroupsLoaded: Promise<void> = Promise.resolve()

  constructor() {
    super()
    createEffect(() => {
      const token = this.store.token()
      if (!token) return
      this.typesAndGroupsLoaded = this.fetchTypesAndGroups(token)
    })
  }

  override async getUpdateComplete(): Promise<boolean> {
    await super.getUpdateComplete()
    await this.typesAndGroupsLoaded
    return super.getUpdateComplete()
  }

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('name') || changedProperties.has('loading')) {
      this.dispatchEvent(new CustomEvent('create-state-changed', {
        bubbles: true,
        composed: true,
        detail: { canSubmit: !!this.name.trim() && !this.loading, loading: this.loading }
      }))
    }
  }

  private async fetchTypesAndGroups(token: string) {
    const [typesEndpoint, groupsEndpoint] = await Promise.all([
      createUrl(orderTemplateTypesPath()),
      createUrl(orderTemplateGroupsPath()),
    ])
    this.typeOptions = await this.typesApi.getOrderTemplateTypes(new GetOrderTemplateTypesRequest(typesEndpoint.href, token))
    if (this.typeOptions[0]) this.selectedType = this.typeOptions[0].type
    this.groups = await this.groupsApi.getOrderTemplateGroups(new GetOrderTemplateGroupsRequest(groupsEndpoint.href, token))
    if (this.groups[0]) this.selectedGroupId = String(this.groups[0].id)
  }

  private handleNameChange(e: Event) {
    this.name = (e.target as HTMLInputElement).value
  }

  private handleGroupChange(e: Event) {
    this.selectedGroupId = (e.target as HTMLInputElement).value
  }

  private handleTypeChange(e: Event) {
    this.selectedType = (e.target as HTMLInputElement).value
  }

  private handleSubmit(e: Event) {
    e.preventDefault()
    void this.createTemplate()
  }

  async submit() {
    void this.createTemplate()
  }

  focusNameInput() {
    this.nameInput?.focus()
  }

  private async createTemplate() {
    if (!this.name.trim() || this.loading) return
    this.loading = true
    try {
      const response = await this.store.createOrderTemplate(this.name.trim(), this.selectedGroupId, this.selectedType)
      if (response.success) {
        this.dispatchEvent(new CustomEvent('order-template-created', { bubbles: true, composed: true, detail: response }))
        this.name = ''
        this.selectedType = this.typeOptions[0]?.type ?? ''
        this.selectedGroupId = this.groups[0] ? String(this.groups[0].id) : ''
        this.formKey++
      }
    } catch { /* noop */ } finally {
      this.loading = false
    }
  }

  override render() {
    return html`
      <form @submit="${this.handleSubmit}">
        <cse-form-field>
          <cse-label slot="label">
            ${t('name_label', { ns: 'csc-create-order-template', default: 'Name order template' })}
          </cse-label>
          ${keyed(this.formKey, html`
            <cse-text-input
              name="name"
              .value="${this.name}"
              @input="${this.handleNameChange}"
              ?disabled="${this.loading}"
            ></cse-text-input>
          `)}
        </cse-form-field>
        ${this.typeOptions.length > 0 ? html`
          <cse-form-field>
            <cse-label>${t('type_label', { ns: 'csc-create-order-template', default: 'Type' })}</cse-label>
            <cse-radio-button-group
              name="type"
              .value="${this.selectedType}"
              @change="${this.handleTypeChange}"
            >
              ${this.typeOptions.map(option => html`
                <cse-radio-button id="${option.type}" value="${option.type}" ?disabled="${this.loading}">
                  ${option.name}
                </cse-radio-button>
              `)}
            </cse-radio-button-group>
          </cse-form-field>
        ` : nothing}
        ${this.groups.length > 0 ? html`
          <cse-form-field>
            <cse-label>${t('group_label', { ns: 'csc-create-order-template', default: 'Group' })}</cse-label>
            <cse-radio-button-group
              name="group"
              .value="${this.selectedGroupId}"
              @change="${this.handleGroupChange}"
            >
              ${this.groups.map(group => html`
                <cse-radio-button id="${group.id}" value="${group.id}" ?disabled="${this.loading}">
                  ${group.name}
                </cse-radio-button>
              `)}
            </cse-radio-button-group>
          </cse-form-field>
        ` : nothing}

        ${!this.isHidden('submit-button') ? html`
          <cse-button
            type="submit"
            variant="primary"
            ?disabled="${!this.name.trim() || this.loading}"
            ?loading="${this.loading}"
          >
            ${t('submit_button', { ns: 'csc-create-order-template', default: 'Create order template' })}
          </cse-button>
        ` : nothing}
      </form>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-create-order-template': CSCCreateOrderTemplate
  }
}
